import { useEffect, useMemo, useState } from 'react';
import { Boxes, Plus, Trash2, X } from 'lucide-react';
import type { CategoriaVista } from '@shared/types';
import { etiquetaDePorcion, porcionesSugeridas, revisarPorciones } from '@shared/porciones';
import { fraccionDeFormatos } from '@shared/fraccionInsumo';
import {
  aCantidadBase,
  aCantidadCapturada,
  decirCantidad,
  esUnidadContinua,
  unidadDeCaptura,
} from '@shared/medidas';
import { pesosACentavos, centavosAPesos } from '@shared/centavos';
import { PrecioPorUnidad, precioDeReferencia } from './PrecioPorUnidad';
import { NotaDeTamanos } from './NotaDeTamanos';
import { conConfirmacionDeNombre } from '@renderer/lib/confirmarNombreParecido';
import { traducirErrorBackend } from '@renderer/lib/mensajesError';

/**
 * Sprint 82 — "Doy de alta lo que vendo, y sus formatos".
 *
 * El formulario que pidió el dueño, con sus palabras y su orden:
 *
 * ```
 * producto: LOMO DE CERDO | unidad de medida: Kilo
 * ¿el kilo lo vendes también en fracciones?
 *   Fracción 1 [0.750] — nombre [3/4] kg
 *   Fracción 2 [0.500] — nombre [1/2] kg
 * ```
 *
 * Y su otra mitad: *"ingreso al inventario del día 10 kg, tiene merma de 40%, por lo tanto para
 * venta me quedan 6 kg"*. Las dos cosas se capturan aquí, una vez.
 *
 * Lo que este formulario elimina: dar de alta el ingrediente en Inventario y **volver a capturar**
 * cada presentación en Productos. Esa segunda captura era nuestro modelo de datos filtrándose a la
 * cara del cliente.
 *
 * El precio se teclea por formato, **nunca se calcula**: en su catálogo real el sirloin de 1 kg
 * cuesta $920 y el de 1/2 kg $505, no $460. Una regla de tres le cambiaría sus precios.
 */

interface FormatoEditable {
  etiqueta: string;
  cantidad: string;
  precio: string;
}

/** Sprint 87 — Exportadas: la edición ofrece exactamente las mismas, o los dos lados se desfasan. */
export const UNIDADES: Array<{ valor: string; etiqueta: string; ayuda: string }> = [
  { valor: 'kg', etiqueta: 'Kilos', ayuda: 'Carne, cortes, algo que se pesa' },
  { valor: 'litro', etiqueta: 'Litros', ayuda: 'Frijoles, salsas, algo que se sirve' },
  { valor: 'pieza', etiqueta: 'Piezas', ayuda: 'Refrescos, panes, algo que se cuenta' },
];

export function AltaConFormatos({
  abierto,
  categorias,
  onCerrar,
  onCreado,
}: {
  abierto: boolean;
  categorias: CategoriaVista[];
  onCerrar: () => void;
  onCreado: () => void;
}) {
  const [nombre, setNombre] = useState('');
  const [unidad, setUnidad] = useState('kg');
  const [categoriaId, setCategoriaId] = useState<string>('');
  const [llevaInventario, setLlevaInventario] = useState(true);
  const [cantidadInicial, setCantidadInicial] = useState('');
  const [merma, setMerma] = useState('');
  const [formatos, setFormatos] = useState<FormatoEditable[]>([]);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Sprint 127 — En qué se teclean las cantidades.
   *
   * `kg` → gramos, `litro` → mililitros, lo que se cuenta → tal cual. El estado de la tabla vive
   * en la unidad de captura y sólo se convierte a la unidad base al validar y al guardar, que son
   * los dos únicos lugares donde el número deja esta pantalla.
   */
  const captura = unidadDeCaptura(unidad);

  /**
   * Al elegir la unidad, la tabla se llena con los tamaños que un restaurante nombra de verdad
   * (Sprint 63): kilo → 1, 3/4, 1/2, 1/4. Piezas → una sola, la unidad completa.
   *
   * Se sugieren **sin precio**: el precio es del negocio, no del sistema.
   */
  useEffect(() => {
    if (!abierto) return;
    setFormatos(
      porcionesSugeridas(unidad).map((p) => ({
        etiqueta: p.etiqueta,
        cantidad: String(aCantidadCapturada(p.cantidad, unidad)),
        precio: '',
      })),
    );
  }, [unidad, abierto]);

  useEffect(() => {
    if (abierto) {
      setNombre('');
      setCategoriaId('');
      setLlevaInventario(true);
      setCantidadInicial('');
      setMerma('');
      setError(null);
      setGuardando(false);
    }
  }, [abierto]);

  const num = (t: string) => {
    const n = Number((t ?? '').replace(',', '.'));
    return Number.isFinite(n) ? n : 0;
  };

  /** "Cargo 10 kg con 40% de merma → para vender me quedan 6 kg". Su ejemplo, en vivo. */
  const disponible = useMemo(() => {
    const cargado = num(cantidadInicial);
    const pct = Math.min(100, Math.max(0, num(merma)));
    return Number((cargado * (1 - pct / 100)).toFixed(3));
  }, [cantidadInicial, merma]);

  const formatosParaValidar = formatos.map((f) => ({
    etiqueta: f.etiqueta,
    cantidad: aCantidadBase(num(f.cantidad), unidad),
    precio: pesosACentavos(num(f.precio)),
  }));
  /**
   * Sprint 127 — En cuánto se parte esto lo dicen **los tamaños que se están capturando**, no una
   * pregunta aparte. Agregar un renglón de 125 g es lo que autoriza los 125 g: la dueña no tiene
   * que saber que existe algo llamado divisibilidad para poder vender lo que vende.
   */
  const fraccionMinima = fraccionDeFormatos(
    formatosParaValidar.map((f) => f.cantidad),
    unidad,
  );
  const problemas = revisarPorciones(formatosParaValidar, unidad, fraccionMinima);

  /** El tamaño más grande manda como referencia de precio: es el que el negocio fija primero. */
  const referenciaPorUnidad = precioDeReferencia(formatosParaValidar);

  if (!abierto) return null;

  const guardar = async () => {
    setError(null);
    if (nombre.trim().length < 2) {
      setError('¿Cómo se llama lo que vas a vender?');
      return;
    }
    const bloqueante = problemas.find((p) => p.bloquea);
    if (bloqueante) {
      setError(`Formato ${bloqueante.indice + 1}: ${bloqueante.mensaje}`);
      return;
    }
    setGuardando(true);
    // Sprint 165 — Si se parece a uno que ya existe ("Coca Zero" / "Coca zero"), se pregunta antes de crear otro.
    const res = await conConfirmacionDeNombre((confirmado) => window.api.productos.altaConFormatos({
      nombreParecidoConfirmado: confirmado || undefined,
      nombre: nombre.trim(),
      unidad,
      categoriaId: categoriaId ? Number(categoriaId) : undefined,
      llevaInventario,
      cantidadInicial: llevaInventario ? num(cantidadInicial) : undefined,
      mermaCoccionPct: llevaInventario ? num(merma) : undefined,
      // La divisibilidad ya no viaja desde aquí: la deduce el servidor de estos mismos formatos.
      formatos: formatosParaValidar,
    }));
    setGuardando(false);
    if (!res.ok) {
      setError(traducirErrorBackend(res.error, res.codigo));
      return;
    }
    await window.alertar({
      titulo: 'Listo',
      mensaje:
        `Se dio de alta "${nombre.trim()}" con ${res.data.productos.length} ` +
        `${res.data.productos.length === 1 ? 'formato de venta' : 'formatos de venta'}` +
        (res.data.disponibleParaVender !== null
          ? `, y ${res.data.disponibleParaVender} ${unidad} listos para vender.`
          : '.'),
      variant: 'success',
    });
    onCreado();
    onCerrar();
  };

  const cambiarFormato = (i: number, campo: keyof FormatoEditable, valor: string) => {
    setFormatos((prev) =>
      prev.map((f, idx) => {
        if (idx !== i) return f;
        const siguiente = { ...f, [campo]: valor };
        // Al cambiar la cantidad, la etiqueta se re-sugiere si el operador no la tocó a mano.
        const etiquetaDe = (t: string) => etiquetaDePorcion(aCantidadBase(num(t), unidad), unidad);
        if (campo === 'cantidad' && f.etiqueta === etiquetaDe(f.cantidad)) {
          siguiente.etiqueta = etiquetaDe(valor);
        }
        return siguiente;
      }),
    );
  };

  const unidadEtiqueta = UNIDADES.find((u) => u.valor === unidad)?.etiqueta.toLowerCase() ?? unidad;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-auto bg-black/50 p-4">
      <div className="card my-4 w-full max-w-2xl">
        <div className="mb-4 flex items-start justify-between gap-2">
          <div>
            <h2 className="text-xl font-bold">Dar de alta algo que vendes</h2>
            <p className="text-sm text-slate-500">
              En un solo paso: qué vendes, en qué tamaños, y cuánto tienes.
            </p>
          </div>
          <button onClick={onCerrar} className="btn-ghost p-2" aria-label="Cerrar">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="label" htmlFor="altaNombre">
                ¿Qué vendes?
              </label>
              <input
                id="altaNombre"
                className="input"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                placeholder="Lomo de Cerdo"
                autoFocus
                maxLength={80}
              />
            </div>
            <div>
              <label className="label" htmlFor="altaCategoria">
                Categoría (opcional)
              </label>
              <select
                id="altaCategoria"
                className="input"
                value={categoriaId}
                onChange={(e) => setCategoriaId(e.target.value)}
              >
                <option value="">— Sin categoría —</option>
                {categorias.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nombre}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <span className="label">¿Cómo lo compras?</span>
            <div className="grid gap-2 sm:grid-cols-3">
              {UNIDADES.map((u) => (
                <button
                  key={u.valor}
                  type="button"
                  onClick={() => setUnidad(u.valor)}
                  className={`min-h-[44px] rounded-lg border-2 px-3 py-2 text-left ${
                    unidad === u.valor
                      ? 'border-primary bg-blue-50'
                      : 'border-slate-200 bg-white hover:bg-slate-50'
                  }`}
                >
                  <span className="block text-sm font-semibold">{u.etiqueta}</span>
                  <span className="block text-xs text-slate-500">{u.ayuda}</span>
                </button>
              ))}
            </div>
          </div>

          {/*
            Sprint 127 — Aquí vivía "¿Se vende partido?" (Sprint 109), y se retiró.

            Era una pregunta de configuración para autorizar algo que la tabla de tamaños de abajo
            ya dice: quien captura un renglón "Medio" está diciendo que lo parte. Preguntarlo antes
            obligaba a la dueña a predecir su propio negocio —"¿y si mañana lo vendo en cuartos?"—
            y cada cambio de opinión costaba entrar aquí a pedir permiso.
          */}

          {/* Lo que se tiene. La segunda mitad de su propuesta. */}
          <div className="card space-y-3 bg-slate-50">
            <label className="flex cursor-pointer items-start gap-2">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4"
                checked={llevaInventario}
                onChange={(e) => setLlevaInventario(e.target.checked)}
              />
              <span className="text-sm">
                <span className="font-medium">Le llevo cuenta de lo que tengo</span>
                <span className="mt-0.5 block text-xs text-slate-500">
                  Si lo apagas se puede vender siempre, sin descontar existencias (una salsa que
                  nunca se acaba, por ejemplo).
                </span>
              </span>
            </label>

            {llevaInventario && (
              <div className="grid gap-3 sm:grid-cols-3">
                <div>
                  <label className="label" htmlFor="altaCantidad">
                    ¿Cuánto tienes hoy? ({unidadEtiqueta})
                  </label>
                  <input
                    id="altaCantidad"
                    className="input"
                    inputMode="decimal"
                    value={cantidadInicial}
                    onChange={(e) => setCantidadInicial(e.target.value)}
                    placeholder="10"
                  />
                </div>
                <div>
                  <label className="label" htmlFor="altaMerma">
                    Merma por cocción (%)
                  </label>
                  <input
                    id="altaMerma"
                    className="input"
                    inputMode="decimal"
                    value={merma}
                    onChange={(e) => setMerma(e.target.value)}
                    placeholder="40"
                  />
                </div>
                <div className="flex flex-col justify-end">
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2">
                    <div className="text-xs text-emerald-700">Para vender te quedan</div>
                    <div className="font-mono text-lg font-bold text-emerald-800">
                      {disponible} {unidad}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Los formatos de venta: su menú. */}
          <div>
            <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
              <span className="label mb-0">
                {unidad === 'pieza'
                  ? '¿A cómo la vendes?'
                  : `¿El ${unidad} lo vendes también en fracciones?`}
              </span>
              <span className="text-xs text-slate-500">
                Cada renglón es un producto en tu menú
              </span>
            </div>

            <div className="space-y-2">
              {formatos.map((f, i) => {
                const problema = problemas.find((p) => p.indice === i);
                return (
                  <div
                    key={i}
                    className={`grid grid-cols-1 gap-2 rounded-lg border p-2 sm:grid-cols-[1fr_1fr_1fr_auto] ${
                      problema?.bloquea ? 'border-rose-300 bg-rose-50' : 'border-slate-200'
                    }`}
                  >
                    <div>
                      <label className="text-xs text-slate-600" htmlFor={`altaCantidad${i}`}>
                        ¿Cuánto lleva? ({captura.nombre})
                      </label>
                      <input
                        id={`altaCantidad${i}`}
                        className="input"
                        inputMode="decimal"
                        value={f.cantidad}
                        onChange={(e) => cambiarFormato(i, 'cantidad', e.target.value)}
                        placeholder={esUnidadContinua(unidad) ? '500' : '1'}
                      />
                    </div>
                    <div>
                      <label className="text-xs text-slate-600" htmlFor={`altaEtiqueta${i}`}>
                        Cómo se llama
                      </label>
                      <input
                        id={`altaEtiqueta${i}`}
                        className="input"
                        value={f.etiqueta}
                        onChange={(e) => cambiarFormato(i, 'etiqueta', e.target.value)}
                        placeholder="1/2 kg"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-slate-600" htmlFor={`altaPrecio${i}`}>
                        Precio
                      </label>
                      <input
                        id={`altaPrecio${i}`}
                        className="input"
                        inputMode="decimal"
                        value={f.precio}
                        onChange={(e) => cambiarFormato(i, 'precio', e.target.value)}
                        placeholder="0.00"
                      />
                      <PrecioPorUnidad
                        precioCentavos={formatosParaValidar[i].precio}
                        cantidadBase={formatosParaValidar[i].cantidad}
                        unidad={unidad}
                        referenciaCentavos={referenciaPorUnidad}
                      />
                    </div>
                    <div className="flex items-end">
                      <button
                        type="button"
                        onClick={() => setFormatos((prev) => prev.filter((_, x) => x !== i))}
                        className="btn-ghost min-h-[44px] px-2 text-rose-700"
                        aria-label="Quitar formato"
                        disabled={formatos.length === 1}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    {problema && (
                      <p
                        className={`sm:col-span-4 text-xs ${
                          problema.bloquea ? 'font-medium text-rose-700' : 'text-amber-700'
                        }`}
                      >
                        {problema.bloquea ? '⚠ ' : '· '}
                        {problema.mensaje}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>

            <button
              type="button"
              onClick={() =>
                setFormatos((prev) => [...prev, { etiqueta: '', cantidad: '', precio: '' }])
              }
              className="btn-ghost mt-2 min-h-[44px] w-full justify-center border border-dashed border-slate-300"
            >
              <Plus className="h-4 w-4" />
              Agregar otro tamaño
            </button>

            <NotaDeTamanos unidad={unidad} fraccionMinima={fraccionMinima} />
          </div>

          {/* Lo que va a quedar, en palabras del negocio. */}
          {nombre.trim().length >= 2 && (
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
              <div className="flex items-center gap-2 font-semibold">
                <Boxes className="h-4 w-4" />
                Va a quedar así
              </div>
              <ul className="mt-1 space-y-0.5">
                {formatos.map((f, i) => (
                  <li key={i}>
                    ·{' '}
                    {formatos.length === 1 &&
                    Math.abs(formatosParaValidar[i].cantidad - 1) < 1e-6
                      ? nombre.trim()
                      : `${nombre.trim()} ${f.etiqueta}`}
                    {f.precio ? ` — $${centavosAPesos(pesosACentavos(num(f.precio))).toFixed(2)}` : ''}
                    {llevaInventario
                      ? ` (descuenta ${decirCantidad(formatosParaValidar[i].cantidad, unidad)})`
                      : ''}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {error && (
            <div className="error-text" role="alert">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-2">
            <button onClick={onCerrar} className="btn-ghost" disabled={guardando}>
              Cancelar
            </button>
            <button onClick={guardar} className="btn-primary min-h-[44px]" disabled={guardando}>
              {guardando ? 'Guardando…' : 'Dar de alta'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
